/**
 * Clerk Home — "Clinical Component Desk"
 * Compact operational indicators + quick actions.
 * No analytics, no rainbow cards.
 */
import React from 'react';
import { ClipboardCheck, PackageCheck, Recycle, Archive, ChevronRight } from 'lucide-react';

const IND = ({ label, value, hint, tone = 'ok', testId }) => (
  <div className="cp-ind" data-testid={testId}>
    <div className="cp-ind-label">
      <em className={tone === 'warn' ? 'is-warn' : tone === 'exc' ? 'is-exc' : ''} />
      {label}
    </div>
    <div className="cp-ind-value">{value}</div>
    <div className="cp-ind-hint">{hint}</div>
  </div>
);

const Quick = ({ icon: Icon, title, desc, onClick, testId }) => (
  <button type="button" className="cp-quick" onClick={onClick} data-testid={testId}>
    <span className="cp-quick-icon"><Icon size={18} strokeWidth={1.7} /></span>
    <div className="cp-quick-title">{title}</div>
    <div className="cp-quick-desc">{desc}</div>
    <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--diis-accent-ink)', fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      Open <ChevronRight size={13} />
    </div>
  </button>
);

export default function HomePage({ ctx }) {
  const { counts, goto, user } = ctx;
  const firstName = (user?.full_name || user?.username || '').split(' ')[0] || (user?.username || '');

  return (
    <div data-testid="cp-home">
      <div className="cp-eyebrow">Clinical Component Desk</div>
      <h1 className="cp-page-title">Good day, {firstName}.</h1>
      <p className="cp-page-lede">
        Issue approved requests and verify returned components. Every action here is
        logged against your clerk account and reconciled with inventory in real time.
      </p>

      <div className="cp-ind-row" role="group" aria-label="Operational indicators">
        <IND
          label="Ready to Issue"
          value={counts.issue}
          hint={counts.issue === 1 ? 'Approved request waiting hand-off' : 'Approved requests waiting hand-off'}
          tone="ok"
          testId="cp-ind-issue"
        />
        <IND
          label="Returns to Verify"
          value={counts.returns}
          hint={counts.returns === 1 ? 'Awaiting physical inspection' : 'Awaiting physical inspection'}
          tone="warn"
          testId="cp-ind-returns"
        />
        <IND
          label="Overdue · Exceptions"
          value={counts.exceptions}
          hint="Items sitting in queue over 3 days"
          tone="exc"
          testId="cp-ind-exceptions"
        />
      </div>

      <div className="cp-quick-grid">
        <Quick
          icon={ClipboardCheck}
          title="Issue Components"
          desc="Review approved requests and hand them over to the student."
          onClick={() => goto('issue')}
          testId="cp-quick-issue"
        />
        <Quick
          icon={PackageCheck}
          title="Verify Returns"
          desc="Inspect returned components, upload verification photos."
          onClick={() => goto('verify')}
          testId="cp-quick-verify"
        />
        <Quick
          icon={Recycle}
          title="Add Used Component"
          desc="Record cleared components back into the reusable pool."
          onClick={() => goto('used')}
          testId="cp-quick-used"
        />
        <Quick
          icon={Archive}
          title="Older Stock Queue"
          desc="Verify older-stock submissions before they join inventory."
          onClick={() => goto('older')}
          testId="cp-quick-older"
        />
      </div>
    </div>
  );
}
